import {Card, DataStatus} from 'platform/components';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {SalesLeagueBranchBoardResponse, SalesLeaguePeriod} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueBoardHeader} from '../../../components/SalesLeagueBoardHeader';
import {SalesLeagueBoardLayout} from '../../../components/SalesLeagueBoardLayout';
import {SalesLeagueBoardSkeleton} from '../../../components/SalesLeagueBoardSkeleton';
import {SALES_LEAGUE_EMPTY_MIN_HEIGHT} from '../../../constants/SALES_LEAGUE_EMPTY_MIN_HEIGHT';
import {SALES_LEAGUE_PERIOD_LABEL} from '../../../constants/SALES_LEAGUE_PERIODS';
import {SalesLeagueBranchBoardKpis} from './SalesLeagueBranchBoardKpis';
import {SalesLeagueBranchBoardPodium} from './SalesLeagueBranchBoardPodium';
import {SalesLeagueBranchBoardTable} from './SalesLeagueBranchBoardTable';

const NO_VEHICLES_SOLD = 0;

interface SalesLeagueBranchBoardViewProps extends RequiredTestIdProps {
  /** Nullish until the query resolves; the board skeletons its contents meanwhile. */
  branchBoard: SalesLeagueBranchBoardResponse | Nullish;
  isLoading: boolean;
  period: SalesLeaguePeriod;
  onPeriodChange: (period: SalesLeaguePeriod) => void;
  onExport: () => void;
  isExporting?: boolean;
}

export function SalesLeagueBranchBoardView(props: SalesLeagueBranchBoardViewProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const branchBoard = props.branchBoard;
  const isLoading = props.isLoading || isNil(branchBoard);

  const periodRangeLabel = isNotNil(branchBoard)
    ? i18n.t('page.salesLeague.periodRange', {
        from: formatDateTime('dateShort', parseDate(branchBoard.period.from)),
        to: formatDateTime('dateShort', parseDate(branchBoard.period.to)),
      })
    : '';

  const periodLabel = i18n.t(SALES_LEAGUE_PERIOD_LABEL[props.period]);

  const subtitle = isNotNil(branchBoard)
    ? i18n.t('page.salesLeague.header.subtitleBranch', {
        range: periodRangeLabel,
        count: formatNumber(branchBoard.totals.branches) ?? EMPTY_PLACEHOLDER,
      })
    : '';

  const header = (
    <SalesLeagueBoardHeader
      heading={i18n.t('module.salesLeague.branchBoard')}
      subtitle={subtitle}
      isLoading={isLoading}
      period={props.period}
      onPeriodChange={props.onPeriodChange}
      onExport={props.onExport}
      isExporting={props.isExporting}
      data-testid={suffixTestId('header', props)}
    />
  );

  // Returning early keeps the remaining branch down to two cases and narrows `branchBoard`
  // for everything below it.
  if (isLoading || isNil(branchBoard)) {
    return (
      <SalesLeagueBoardLayout header={header}>
        <SalesLeagueBoardSkeleton />
      </SalesLeagueBoardLayout>
    );
  }

  const hasNoBranches = isEmpty(branchBoard.rows);
  const hasNothingSoldAcrossAllBranches = branchBoard.totals.vehiclesSold === NO_VEHICLES_SOLD;

  // A dead period is still an empty board: the backend ranks every branch at zero, and a
  // podium of zeros would award a medal for selling nothing.
  const isBoardEmpty = hasNoBranches || hasNothingSoldAcrossAllBranches;

  return (
    <SalesLeagueBoardLayout header={header}>
      {isBoardEmpty ? (
        <Card>
          <DataStatus
            isEmpty
            emptyMessage={i18n.t('page.salesLeague.branchBoard.empty')}
            emptySubheadline={i18n.t('page.salesLeague.emptySubheadline')}
            minHeight={SALES_LEAGUE_EMPTY_MIN_HEIGHT}
            data-testid={suffixTestId('empty', props)}
          />
        </Card>
      ) : (
        <>
          <SalesLeagueBranchBoardPodium
            branchBoard={branchBoard}
            periodLabel={periodLabel}
            data-testid={suffixTestId('podium', props)}
          />
          <SalesLeagueBranchBoardKpis
            branchBoard={branchBoard}
            periodLabel={periodLabel}
            data-testid={suffixTestId('kpi', props)}
          />
          <SalesLeagueBranchBoardTable
            key={props.period}
            branchBoard={branchBoard}
            period={props.period}
            periodLabel={periodLabel}
            data-testid={suffixTestId('branchBoard', props)}
          />
        </>
      )}
    </SalesLeagueBoardLayout>
  );
}
